
import * as THREE from "three";
import type { MeshEffectDriver } from "../MeshEffectDriver";
import type {
  EffectArchetype,
  ActiveEffects,
  WindEffectParams,
} from "../types";

// ---------------------------------------------------------------------------
// Shared uniforms — one set of objects referenced by ALL vegetation materials.
// Writing to .value here updates every tree/grass instance in one shot.
// ---------------------------------------------------------------------------

const sharedUniforms = {
  uTime: { value: 0 },
  uWindDirection: { value: new THREE.Vector2(1, 0) },
  uWindStrength: { value: 0 },
};

// ---------------------------------------------------------------------------
// GLSL snippets
// ---------------------------------------------------------------------------

/** Uniform declarations injected into the vertex shader. */
const WIND_UNIFORMS = /* glsl */ `
  uniform float uTime;
  uniform vec2 uWindDirection;
  uniform float uWindStrength;
  uniform float uStiffness;
  uniform float uAmplitude;
  uniform float uStiffnessMul;
`;

/**
 * Vertex displacement injected before the final position assignment.
 *
 * Strategy:
 *   - heightFactor: normalised Y within the bounding box (0 at base, 1 at top).
 *     Roots stay still; canopy sways the most.
 *   - sway: two overlapping sine waves for natural, non-repeating motion.
 *   - displacement is a vec3 along the XZ wind direction, scaled by all factors.
 */
const WIND_VERTEX_DISPLACEMENT = /* glsl */ `
  // --- Wind sway (VegetationWindDriver) ---
  float _windMaxY = 6.0;
  float _windHeightFactor = clamp(transformed.y / _windMaxY, 0.0, 1.0);
  float _windSway =
    sin(uTime * 1.5 + transformed.y * 2.0) * 0.6 +
    sin(uTime * 2.3 + transformed.y * 3.1 + 1.0) * 0.4;
  float _windDisplacementAmount =
    _windSway *
    uWindStrength *
    uAmplitude *
    (1.0 - uStiffness) *
    uStiffnessMul *
    _windHeightFactor;
  transformed.x += uWindDirection.x * _windDisplacementAmount;
  transformed.z += uWindDirection.y * _windDisplacementAmount;
`;

// ---------------------------------------------------------------------------
// Material-name → stiffness multiplier
// ---------------------------------------------------------------------------

/**
 * Returns a stiffness multiplier based on the material's name.
 * Leaves sway the most, trunk barely moves.
 */
function getStiffnessMultiplier(materialName: string): number {
  const name = materialName.toLowerCase();
  if (
    name.includes("leaf") ||
    name.includes("leaves") ||
    name.includes("foliage")
  )
    return 1.0;
  if (name.includes("branch") || name.includes("twig")) return 0.6;
  if (name.includes("trunk") || name.includes("stem") || name.includes("bark"))
    return 0.2;
  // Default: moderate sway (flowers, grass, generic vegetation parts)
  return 0.7;
}

// ---------------------------------------------------------------------------
// Bookkeeping: track modified materials for update() and dispose()
// ---------------------------------------------------------------------------

interface ModifiedEntry {
  material: THREE.MeshStandardMaterial;
  originalOnBeforeCompile: THREE.MeshStandardMaterial["onBeforeCompile"];
  originalCustomProgramCacheKey: THREE.MeshStandardMaterial["customProgramCacheKey"];
}

// ---------------------------------------------------------------------------
// VegetationWindDriver
// ---------------------------------------------------------------------------

export class VegetationWindDriver implements MeshEffectDriver<WindEffectParams> {
  readonly capability = "vegetation-wind";

  private modifiedMaterials: ModifiedEntry[] = [];

  canApply(archetype: EffectArchetype): boolean {
    return archetype === "vegetation";
  }

  apply(
    target: THREE.Object3D,
    params: WindEffectParams,
    _global: ActiveEffects,
  ): void {
    target.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;

      const material = child.material;
      if (!(material instanceof THREE.MeshStandardMaterial)) return;

      // Idempotency guard — never apply twice to the same material instance
      const mat = material as THREE.MeshStandardMaterial & {
        __windApplied?: boolean;
      };
      if (mat.__windApplied) return;
      mat.__windApplied = true;

      const stiffnessMul = getStiffnessMultiplier(mat.name);

      // Per-instance uniforms (unique to this material)
      const uStiffness = { value: params.stiffness };
      const uAmplitude = { value: params.amplitude };
      const uStiffnessMul = { value: stiffnessMul };

      // Save originals for dispose()
      const entry: ModifiedEntry = {
        material: mat,
        originalOnBeforeCompile: mat.onBeforeCompile,
        originalCustomProgramCacheKey: mat.customProgramCacheKey,
      };
      this.modifiedMaterials.push(entry);

      mat.customProgramCacheKey = () => "vegetation-wind";

      mat.onBeforeCompile = (
        shader: THREE.WebGLProgramParametersWithUniforms,
        _renderer?: THREE.WebGLRenderer,
      ) => {
        // Inject shared + per-instance uniforms
        shader.uniforms.uTime = sharedUniforms.uTime;
        shader.uniforms.uWindDirection = sharedUniforms.uWindDirection;
        shader.uniforms.uWindStrength = sharedUniforms.uWindStrength;
        shader.uniforms.uStiffness = uStiffness;
        shader.uniforms.uAmplitude = uAmplitude;
        shader.uniforms.uStiffnessMul = uStiffnessMul;

        // Inject uniform declarations
        shader.vertexShader = shader.vertexShader.replace(
          "#include <common>",
          `#include <common>\n${WIND_UNIFORMS}`,
        );

        // Inject displacement before final position assignment
        shader.vertexShader = shader.vertexShader.replace(
          "#include <begin_vertex>",
          `#include <begin_vertex>\n${WIND_VERTEX_DISPLACEMENT}`,
        );
      };

      // Force Three.js to recompile this material
      mat.needsUpdate = true;
    });
  }

  /**
   * Tick shared uniforms. Called once per frame by EffectManager.
   * All vegetation instances pick up the changes automatically because
   * they reference the same shared uniform objects.
   */
  update(global: ActiveEffects, time: number): void {
    sharedUniforms.uTime.value = time;

    if (global.wind) {
      sharedUniforms.uWindDirection.value.set(
        global.wind.direction[0],
        global.wind.direction[1],
      );
      sharedUniforms.uWindStrength.value = global.wind.strength;
    } else {
      sharedUniforms.uWindStrength.value = 0;
    }
  }

  dispose(): void {
    for (const entry of this.modifiedMaterials) {
      const mat = entry.material as THREE.MeshStandardMaterial & {
        __windApplied?: boolean;
      };
      mat.onBeforeCompile =
        entry.originalOnBeforeCompile ??
        (() => {
          /* noop */
        });
      mat.customProgramCacheKey =
        entry.originalCustomProgramCacheKey ?? (() => "");
      mat.needsUpdate = true;
      delete mat.__windApplied;
    }
    this.modifiedMaterials.length = 0;
  }
}
